/**
 * `CallToActionButton` is a functional React component that renders a button
 * which can be used as a call to action (CTA).
 *
 * The component utilizes the `useSearchParamOrDefaultProps` hook to extract
 * and prioritize certain properties either from search parameters or from
 * the provided default options.
 *
 * The rendered button is wrapped within a `Link` component, which enables navigation
 * to a specified `articleLink`, appended with analytics parameters.
 *
 * The component can be styled using the provided `classNames` prop and the default
 * styles from the 'CallToActionButton.module.css' file. Additionally, an optional icon
 * can be provided to be rendered alongside the button's label.
 *
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} [props.icon=null] - An optional icon to display alongside the button label.
 * @param {React.ReactNode} [props.children=null] - Child elements or components to be rendered as the button label.
 *
 * @returns {JSX.Element} - The rendered call to action button component wrapped in a link.
 */

import useSearchParamOrDefaultProps from '../hooks/useSearchParamOrDefaultProps.js';
import styles from './CallToActionButton.module.css';
import PropTypes from 'prop-types';
import Link from './Link.js';
import urlJoinParameters from '../utils/urlJoinParameters.js';

const CallToActionButton = ({ icon, children }) => {
  const { callToActionButtonText, articleLink, __source, tpcc } =
    useSearchParamOrDefaultProps();

  const to = urlJoinParameters(articleLink, { __source, tpcc });

  return (
    <div className={styles.callToActionButtonWrapper}>
      <Link to={to}>
        {icon}
        {children ? children : callToActionButtonText}
      </Link>
    </div>
  );
};

CallToActionButton.displayName = 'CallToActionButton';

CallToActionButton.defaultProps = {
  icon: null,
  children: null,
};

CallToActionButton.propTypes = {
  icon: PropTypes.node,
  children: PropTypes.node,
};

export default CallToActionButton;
